import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { ArrowRight, Smartphone, Tablet, Monitor } from 'lucide-react'

const AppsSection = () => {
  const apps = [
    {
      id: 'aluno',
      name: 'App do Aluno',
      description: 'Acompanhe seu progresso, veja avaliações, troféus conquistados e gerencie pagamentos.',
      icon: Smartphone,
      color: 'from-blue-500 to-cyan-500',
      bgColor: 'bg-blue-50',
      features: [
        'Visualize seu nível atual e requisitos',
        'Acompanhe avaliações dos professores',
        'Sistema de troféus e conquistas',
        'Gestão de mensalidades e pagamentos',
        'Avalie a escola e professores'
      ],
      screenshot: '/SwimmingPool Aluno/prints iphone e android/tela_home.jpeg',
      platforms: ['iOS', 'Android']
    },
    {
      id: 'escola',
      name: 'App da Escola',
      description: 'Gerencie alunos, turmas, professores, mensalidades e tenha controle total da sua escola.',
      icon: Monitor,
      color: 'from-green-500 to-emerald-500',
      bgColor: 'bg-green-50',
      features: [
        'Gestão completa de alunos e turmas',
        'Controle de mensalidades e receitas',
        'Relatórios financeiros detalhados',
        'Agenda diária e semanal',
        'Sistema de mensagens interno'
      ],
      screenshot: '/SwimmingPool Escola/prints Iphone 16 pro max/tela_home_rsumo_geral.png',
      platforms: ['Web', 'iOS', 'Android']
    },
    {
      id: 'professor',
      name: 'App do Professor',
      description: 'Gerencie avaliações, acompanhe alunos e mantenha comunicação direta através de um dashboard completo.',
      icon: Tablet,
      color: 'from-purple-500 to-pink-500',
      bgColor: 'bg-purple-50',
      features: [
        'Dashboard inicial com visão geral',
        'Sistema completo de avaliações',
        'Listagem de alunos para avaliação',
        'Histórico de avaliações realizadas',
        'Chat com alunos e conversas'
      ],
      screenshot: '/SwimmingPool Professor/tela_dashboard_inicial.jpeg',
      platforms: ['iOS', 'Android']
    }
  ]

  return (
    <section className="py-20 bg-gray-50">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl lg:text-4xl font-bold text-dark-900 mb-4">
            Nossos Apps Especializados
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Cada app foi desenvolvido especificamente para atender às necessidades 
            de alunos, professores e gestores de escolas de natação.
          </p>
        </motion.div>

        {/* Apps Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16">
          {apps.map((app, index) => (
            <motion.div
              key={app.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.2 }}
              className="group"
            >
              <div className={`${app.bgColor} rounded-2xl p-8 h-full hover:shadow-xl transition-all duration-300 group-hover:-translate-y-2`}>
                {/* App Icon */}
                <div className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-r ${app.color} text-white mb-6`}>
                  <app.icon className="h-8 w-8" />
                </div>

                {/* App Info */}
                <h3 className="text-2xl font-bold text-dark-900 mb-3">
                  {app.name}
                </h3>
                
                <p className="text-gray-600 mb-6 leading-relaxed">
                  {app.description}
                </p>

                {/* Features */}
                <ul className="space-y-3 mb-6">
                  {app.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-start space-x-3">
                      <div className={`w-2 h-2 rounded-full bg-gradient-to-r ${app.color} mt-2 flex-shrink-0`}></div>
                      <span className="text-sm text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>

                {/* Platforms */}
                <div className="flex items-center space-x-2 mb-6">
                  {app.platforms.map((platform) => (
                    <span 
                      key={platform}
                      className="px-3 py-1 bg-white rounded-full text-xs font-medium text-gray-700"
                    >
                      {platform}
                    </span>
                  ))}
                </div>

                {/* Screenshot */}
                <div className="mb-6">
                  <img 
                    src={app.screenshot} 
                    alt={`${app.name} Screenshot`}
                    className="w-full h-auto rounded-lg shadow-lg border border-gray-200"
                  />
                </div>

                {/* CTA */}
                <Link 
                  to={`/apps#${app.id}`}
                  className="inline-flex items-center text-primary-600 hover:text-primary-700 font-medium transition-colors"
                >
                  Saiba mais
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="text-center"
        >
          <p className="text-gray-600 mb-6">
            Quer conhecer todos os detalhes de cada app?
          </p>
          <Link to="/apps" className="btn-primary">
            Ver Todos os Apps
          </Link>
        </motion.div>
      </div>
    </section>
  )
}

export default AppsSection
